import React, { useEffect, useState } from "react";
import { confirmable } from "react-confirm";
import { Row, message, Input, Select, Checkbox, Button, Card } from "antd";
import { PictureOutlined, FileTextOutlined } from "@ant-design/icons";
import Modal from "antd/lib/modal/Modal";
import { Editor } from "react-draft-wysiwyg";
import { EditorState, convertToRaw } from "draft-js";
import draftToHtml from "draftjs-to-html";
import "react-draft-wysiwyg/dist/react-draft-wysiwyg.css";
import "./MessageModal.css";
import { CloseOutlined } from "@ant-design/icons";
import { fetchAudienceList } from "services/audienceService";
import { sendEmailCampaign } from "services/emailCampaignService";

function MessageModal(props) {
  const { show, proceed, title, dataList } = props;

  const [selectedToAudiences, setSelectedToAudiences] = useState([]);
  const [selectedCCAudiences, setSelectedCCAudiences] = useState([]);
  const [selectedBCCAudiences, setSelectedBCCAudiences] = useState([]);
  const [editorState, setEditorState] = useState(EditorState.createEmpty());
  const [subject, setSubject] = useState("");
  const [content, setContent] = useState("");
  const [from, setFrom] = useState(props?.from || "");
  const [templateContent, setTemplateContent] = useState("");
  const [selFile, setSelFile] = useState(null);
  const [isSending, setIsSending] = useState(false);
  // Wizard state
  const [step, setStep] = useState(1);
  const [templateType, setTemplateType] = useState("");
  const [useDefaultLogo, setUseDefaultLogo] = useState(true);
  const [logoFile, setLogoFile] = useState(null);
  const [assetFile, setAssetFile] = useState(null);
  const [logoBlobUrl, setLogoBlobUrl] = useState(null);
  const [assetBlobUrl, setAssetBlobUrl] = useState(null);
  const [audienceList, setAudienceList] = useState([]);

  // Load audiences when modal opens
  useEffect(() => {
    async function loadAudiences() {
      const token = props?.token;
      const clubId = props?.club_id;
      if (token && show) {
        const list = await fetchAudienceList(token, clubId);
        // Only show email audiences in the email modal
        const emailOnly = (Array.isArray(list) ? list : []).filter(
          (a) => (a.channel || "email") !== "sms"
        );
        setAudienceList(emailOnly);
      }
    }
    if (show) {
      // ensure from is set to incoming prop when opening
      setFrom(props?.from || "");
      loadAudiences();
    }
  }, [show, props?.token]);

  // Reset form when modal closes
  useEffect(() => {
    if (!show) {
      setSelectedToAudiences([]);
      setSelectedCCAudiences([]);
      setSelectedBCCAudiences([]);
      setEditorState(EditorState.createEmpty());
      setSubject("");
      setContent("");
      setTemplateContent("");
      setSelFile(null);
      setStep(1);
      setTemplateType("");
      setUseDefaultLogo(true);
      setLogoFile(null);
      setAssetFile(null);
      // Clean up blob URLs
      if (logoBlobUrl) {
        URL.revokeObjectURL(logoBlobUrl);
        setLogoBlobUrl(null);
      }
      if (assetBlobUrl) {
        URL.revokeObjectURL(assetBlobUrl);
        setAssetBlobUrl(null);
      }
    }
  }, [show]);

  // Create blob URLs when files change
  useEffect(() => {
    let currentUrl = null;
    
    if (logoFile && !useDefaultLogo) {
      if (logoBlobUrl) {
        URL.revokeObjectURL(logoBlobUrl);
      }
      currentUrl = URL.createObjectURL(logoFile);
      setLogoBlobUrl(currentUrl);
    } else if (useDefaultLogo || !logoFile) {
      if (logoBlobUrl) {
        URL.revokeObjectURL(logoBlobUrl);
        setLogoBlobUrl(null);
      }
    }
    
    return () => {
      if (currentUrl) {
        URL.revokeObjectURL(currentUrl);
      }
    };
  }, [logoFile, useDefaultLogo]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    let currentUrl = null;
    
    if (assetFile) {
      if (assetBlobUrl) {
        URL.revokeObjectURL(assetBlobUrl);
      }
      currentUrl = URL.createObjectURL(assetFile);
      setAssetBlobUrl(currentUrl);
    } else {
      if (assetBlobUrl) {
        URL.revokeObjectURL(assetBlobUrl);
        setAssetBlobUrl(null);
      }
    }
    
    return () => {
      if (currentUrl) {
        URL.revokeObjectURL(currentUrl);
      }
    };
  }, [assetFile]); // eslint-disable-line react-hooks/exhaustive-deps

  const onEditorStateChange = (e) => {
    if (e != null) {
      let selStr = draftToHtml(convertToRaw(e.getCurrentContent()));
      // Always combine template with editor content (editor content replaces previous editor content)
      // templateContent is set in onNext and doesn't change
      const combinedContent = templateContent + (selStr && selStr.trim() ? `<div>${selStr}</div>` : "");
      setContent(combinedContent);
      setEditorState(e);
    }
  };

  // Helper function to convert File to base64
  const fileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = (error) => reject(error);
    });
  };

  // Extract emails from selected audiences
  const getEmailsFromAudiences = (audienceIds) => {
    const emails = new Set();
    const audienceNames = [];
    audienceIds.forEach((audienceId) => {
      const audience = audienceList.find((a) => a.id === audienceId);
      if (audience) {
        audienceNames.push(audience.name);
        if (Array.isArray(audience.emails)) {
          audience.emails.forEach((email) => emails.add(email));
        }
      }
    });
    return { emails: Array.from(emails), audienceNames };
  };

  const onCreate = async () => {
    if (subject.trim() === "") {
      message.error("Please input subject");
      return;
    }
    if (selectedToAudiences.length === 0) {
      message.error("Please select at least one audience for 'To'");
      return;
    }
    if (!content || content.trim() === "") {
      message.error("Please input content");
      return;
    }

    setIsSending(true);
    try {
      // Extract emails from audiences
      const { emails: toEmails, audienceNames: toAudienceNames } = getEmailsFromAudiences(selectedToAudiences);
      const { emails: ccEmails, audienceNames: ccAudienceNames } = getEmailsFromAudiences(selectedCCAudiences);
      const { emails: bccEmails, audienceNames: bccAudienceNames } = getEmailsFromAudiences(selectedBCCAudiences);

      if (toEmails.length === 0) {
        message.error("Selected audiences have no email addresses");
        setIsSending(false);
        return;
      }

      // Combine all audience names for display
      const allAudienceNames = [...toAudienceNames, ...ccAudienceNames, ...bccAudienceNames];

      // Prepare attachment if present
      let attachment = null;
      if (selFile) {
        try {
          const base64Data = await fileToBase64(selFile);
          attachment = {
            file_name: selFile.name,
            file_base64: base64Data,
          };
        } catch (error) {
          message.error("Failed to process attachment");
          setIsSending(false);
          return;
        }
      }

      // Build final HTML content with base64 data URLs for images
      // First, build the template HTML with base64 images
      const templateHtmlWithBase64 = await buildFinalHtml();
      // Extract editor content from current content (remove template HTML)
      const editorContent = content.replace(templateContent, '').trim();
      // Combine template with editor content
      const finalHtml = templateHtmlWithBase64 + (editorContent ? `<div>${editorContent}</div>` : "");

      // Prepare payload
      const payload = {
        token: props.token,
        club_id: props.club_id,
        from: from && from.trim() ? from.trim() : undefined,
        subject: subject.trim(),
        html: finalHtml,
        to_emails: toEmails,
        cc_emails: ccEmails.length > 0 ? ccEmails : undefined,
        bcc_emails: bccEmails.length > 0 ? bccEmails : undefined,
        audience_names: allAudienceNames,
        attachment: attachment,
      };

  // Remove undefined fields
  Object.keys(payload).forEach((key) => payload[key] === undefined && delete payload[key]);

  // Send campaign
  const result = await sendEmailCampaign(payload);

      message.success(`Email sent successfully to ${result.recipients_count || toEmails.length} recipients!`);
      
      proceed({ success: true, campaign_id: result.campaign_id });
    } catch (error) {
      console.error("[SendEmailCampaign] error:", error);
      message.error(error || "Failed to send email campaign");
    } finally {
      setIsSending(false);
    }
  };
  const onDropImage = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    if (files[0].size / 1000 / 1000 > 5) {
      message.error("File size should not be greater than 5Mb.");
      return;
    }
    setSelFile(files[0]);
  };

  const onUploadLogo = (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    setLogoFile(files[0]);
    setUseDefaultLogo(false);
  };
  const onUploadAsset = (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    setAssetFile(files[0]);
  };

  // Build preview HTML using blob URLs (for display only)
  const buildPreviewHtml = () => {
    const logoSrc = useDefaultLogo
      ? "/assets/images/logo.png"
      : (logoBlobUrl || "/assets/images/logo.png");
    const assetSrc = assetBlobUrl || null;
    let html = `<div style="text-align:center;padding:16px">`;
    if (templateType === "logo-with-message" || templateType === "logo-with-image" || templateType === "logo-with-image-and-message") {
      html += `<img src="${logoSrc}" alt="Logo" style="max-width:180px;margin-bottom:16px"/>`;
    }
    if (templateType === "logo-with-image" || templateType === "logo-with-image-and-message") {
      if (assetSrc) html += `<div><img src="${assetSrc}" alt="Image" style="max-width:100%"/></div>`;
    }
    html += `</div>`;
    return html;
  };

  // Build final HTML with base64 data URLs (for sending)
  const buildFinalHtml = async () => {
    let logoSrc = useDefaultLogo
      ? "/assets/images/logo.png"
      : null;
    
    // Convert logo file to base64 if not using default
    if (!useDefaultLogo && logoFile) {
      try {
        logoSrc = await fileToBase64(logoFile);
      } catch (error) {
        console.error("Failed to convert logo to base64:", error);
        logoSrc = "/assets/images/logo.png";
      }
    }
    
    // Convert asset file to base64
    let assetSrc = null;
    if (assetFile) {
      try {
        assetSrc = await fileToBase64(assetFile);
      } catch (error) {
        console.error("Failed to convert asset to base64:", error);
      }
    }

    let html = `<div style="text-align:center;padding:16px">`;
    if (templateType === "logo-with-message" || templateType === "logo-with-image" || templateType === "logo-with-image-and-message") {
      html += `<img src="${logoSrc || '/assets/images/logo.png'}" alt="Logo" style="max-width:180px;margin-bottom:16px"/>`;
    }
    if (templateType === "logo-with-image" || templateType === "logo-with-image-and-message") {
      if (assetSrc) html += `<div><img src="${assetSrc}" alt="Image" style="max-width:100%"/></div>`;
    }
    html += `</div>`;
    return html;
  };

  const onNext = () => {
    if (subject.trim() === "") {
      message.error("Please input subject");
      return;
    }
    if (selectedToAudiences.length === 0) {
      message.error("Please select at least one audience for 'To'");
      return;
    }
    if (!templateType) {
      message.error("Please select a template");
      return;
    }
    // Only validate logo upload for templates that require logos
    if ((templateType === "logo-with-message" || templateType === "logo-with-image" || templateType === "logo-with-image-and-message") && !useDefaultLogo && !logoFile) {
      message.error("Please upload a logo or check 'Use default'");
      return;
    }
    // Only validate image upload for templates that require images
    if ((templateType === "logo-with-image" || templateType === "logo-with-image-and-message") && !assetFile) {
      message.error("Please upload an image for the selected template");
      return;
    }
    // Build preview HTML with blob URLs for display
    const templateHtml = buildPreviewHtml();
    setTemplateContent(templateHtml);
    setContent(templateHtml);
    setStep(2);
  };

  const onBack = () => setStep(1);

  return (
    <Modal
      open={show}
      destroyOnClose={true}
      onCancel={() => {
        proceed(null);
      }}
      className="message-dlg"
      closable={true}
      maskClosable={false}
      title={step === 1 ? "Send Mail" : "Email Content"}
      footer={null}
    >
      {step === 1 && (
        <>
          <Row style={{ marginTop: 10 }}>
            <div className="field-label">From:</div>
            <Input
              className="custom-input"
              value={from}
              onChange={(e) => setFrom(e.target.value)}
              placeholder="Sender name or email"
            />
          </Row>
          <Row style={{ marginTop: 10 }}>
            <div className="field-label required">Subject</div>
            <Input 
              className="custom-input" 
              value={subject} 
              onChange={(e) => setSubject(e.target.value)}
              placeholder="Enter email subject"
            />
          </Row>
          <Row style={{ marginTop: 10 }}>
            <div className="field-label required">To (Audiences)</div>
            <Select
              mode="multiple"
              placeholder="Select audiences"
              allowClear
              value={selectedToAudiences}
              onChange={(val) => setSelectedToAudiences(val || [])}
              options={audienceList.map((a) => ({ label: `${a.name} (${a.email_count || 0} recipients)`, value: a.id }))}
              style={{ width: "100%" }}
            />
          </Row>
          <Row style={{ marginTop: 10 }}>
            <div className="field-label">CC (Audiences)</div>
            <Select
              mode="multiple"
              placeholder="Select audiences for CC (optional)"
              allowClear
              value={selectedCCAudiences}
              onChange={(val) => setSelectedCCAudiences(val || [])}
              options={audienceList.map((a) => ({ label: `${a.name} (${a.email_count || 0} recipients)`, value: a.id }))}
              style={{ width: "100%" }}
            />
          </Row>
          <Row style={{ marginTop: 10 }}>
            <div className="field-label">BCC (Audiences)</div>
            <Select
              mode="multiple"
              placeholder="Select audiences for BCC (optional)"
              allowClear
              value={selectedBCCAudiences}
              onChange={(val) => setSelectedBCCAudiences(val || [])}
              options={audienceList.map((a) => ({ label: `${a.name} (${a.email_count || 0} recipients)`, value: a.id }))}
              style={{ width: "100%" }}
            />
          </Row>
          <Row style={{ marginTop: 10 }}>
            <div style={{ marginBottom: 8, fontWeight: 600, width: '100%' }}>Template</div>
            <div className="template-grid">
              {[
                { key: 'logo-with-message', label: 'Logo with Message', icon: <><PictureOutlined /> <FileTextOutlined /></> },
                { key: 'logo-with-image', label: 'Logo with Image', icon: <PictureOutlined /> },
                { key: 'logo-with-image-and-message', label: 'Logo with Image and Message', icon: <><PictureOutlined /> <FileTextOutlined /></> },
                { key: 'message-only', label: 'Message only', icon: <FileTextOutlined /> },
              ].map(t => (
                <div
                  key={t.key}
                  className={`template-card ${templateType === t.key ? 'selected' : ''}`}
                  onClick={() => setTemplateType(t.key)}
                >
                  <div className="template-thumb template-icons">{t.icon}</div>
                  <div className="template-title">{t.label}</div>
                </div>
              ))}
            </div>
          </Row>
          {/* Only show logo upload for templates that require logos */}
          {(templateType === "logo-with-message" || templateType === "logo-with-image" || templateType === "logo-with-image-and-message") && (
            <div className="upload-section">
              <div className="message-upload-row">
                <div className="label-left">Upload Logo</div>
                <Checkbox checked={useDefaultLogo} onChange={(e)=>setUseDefaultLogo(e.target.checked)}>Use default</Checkbox>
                <label className="custom-button common-button">
                  <input type="file" accept="image/*" style={{ display: "none" }} onChange={onUploadLogo} />
                  Choose File
                </label>
                {logoFile && <>
                  <img className="preview-thumb" src={logoBlobUrl || URL.createObjectURL(logoFile)} alt="logo" />
                  <span className="file-name">{logoFile.name}</span>
                </>}
              </div>
            </div>
          )}
          {/* Only show image upload for templates that require images */}
          {(templateType === "logo-with-image" || templateType === "logo-with-image-and-message") && (
            <div className="upload-section">
              <div className="message-upload-row">
                <div className="label-left">Upload Image or Document</div>
                <label className="custom-button common-button">
                  <input type="file" accept="image/*,application/pdf" style={{ display: "none" }} onChange={onUploadAsset} />
                  Choose File
                </label>
                {assetFile && <>
                  {assetFile.type.startsWith('image/') && (
                    <img className="preview-thumb" src={assetBlobUrl || URL.createObjectURL(assetFile)} alt="asset" />
                  )}
                  <span className="file-name">{assetFile.name}</span>
                </>}
              </div>
            </div>
          )}
          <div style={{ marginTop: 16, textAlign: 'right' }}>
            <Button onClick={() => proceed(null)} style={{ marginRight: 8 }}>Cancel</Button>
            <Button type="primary" onClick={onNext}>Next</Button>
          </div>
        </>
      )}

      {step === 2 && (
        <>
          <div style={{ marginTop: 10 }}>
            <Card size="small" style={{ border: '1px solid #e8e8e8' }}>
              <div dangerouslySetInnerHTML={{ __html: buildPreviewHtml() }} />
            </Card>
          </div>
          {(templateType === 'logo-with-message' || templateType === 'logo-with-image-and-message' || templateType === 'message-only') && (
            <div className="editor-rect" style={{ marginTop: 10 }}>
              <Editor
                editorState={editorState}
                toolbarClassName="toolbarClassName"
                wrapperClassName="wrapperClassName"
                editorClassName="editorClassName"
                onEditorStateChange={onEditorStateChange}
              />
            </div>
          )}
          <div className="new_image_uploader">
            <label htmlFor="new_image_uploader">
              <input type="file" id="new_image_uploader" name="new_image_uploader" style={{ display: "none" }} accept="*" onChange={onDropImage} />
              <span className="attach-file-button">Attach File</span>
            </label>
            {selFile && (
              <div className="attached-file-info">
                <div className="attached-file-icon">
                  <FileTextOutlined style={{ fontSize: '16px', color: '#6c757d' }} />
                </div>
                <div className="attached-file-details">
                  <div className="attached-file-name">
                    {selFile.name}
                  </div>
                  <div className="attached-file-meta">
                    <span>{selFile.type || 'Unknown type'}</span>
                    <span>•</span>
                    <span>{selFile.size ? `${(selFile.size / 1024).toFixed(1)} KB` : 'Unknown size'}</span>
                  </div>
                </div>
                <CloseOutlined 
                  className="attached-file-remove"
                  onClick={() => setSelFile(null)}
                />
              </div>
            )}
            {/* debug log removed */}
          </div>
          <div className="message-modal-step2-buttons full-width">
            <Button onClick={onBack}>Back</Button>
            <Button 
              type="primary" 
              onClick={onCreate}
              loading={isSending}
              disabled={isSending}
            >
              {isSending ? "Sending..." : "Send"}
            </Button>
          </div>
        </>
      )}
    </Modal>
  );
}

export default confirmable(MessageModal);
